import { scrollMainContentToTop } from '@/shared/utils/scrollMainContent';
import { CFormSelect, CPagination, CPaginationItem } from '@coreui/react';
import React from 'react';
import { DEFAULT_PER_PAGE_OPTIONS } from './constants';
import { useIsMobile } from './useIsMobile';

export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  perPage: number;
  total?: number;
  perPageOptions?: number[];
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: number) => void;
  className?: string;
  paginationClassName?: string;
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  perPage,
  total,
  perPageOptions = DEFAULT_PER_PAGE_OPTIONS,
  onPageChange,
  onPerPageChange,
  className = '',
  paginationClassName = '',
}) => {
  const isMobile = useIsMobile();
  const hasNextPage = currentPage < totalPages;
  const hasPrevPage = currentPage > 1;
  const startItem = total && total > 0 ? (currentPage - 1) * perPage + 1 : 0;
  const endItem = total && total > 0 ? Math.min(currentPage * perPage, total) : 0;

  const getPaginationText = () => {
    if (!total || total === 0) {
      return 'Showing 0 of 0 items';
    }
    return `Showing ${startItem} to ${endItem} of ${total} items`;
  };

  const getVisiblePages = () => {
    if (isMobile && totalPages > 4) {
      if (currentPage <= 2) {
        return [1, 2, '...', totalPages];
      }

      if (currentPage >= totalPages - 1) {
        return [1, '...', totalPages - 1, totalPages];
      }

      return [1, '...', currentPage, '...', totalPages];
    }

    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const result: (number | string)[] = [];

    result.push(1);

    if (currentPage <= 4) {
      for (let i = 2; i <= 5; i++) {
        result.push(i);
      }
      result.push('...');
      result.push(totalPages);
    } else if (currentPage >= totalPages - 3) {
      result.push('...');
      for (let i = totalPages - 4; i <= totalPages; i++) {
        result.push(i);
      }
    } else {
      result.push('...');
      result.push(currentPage - 1);
      result.push(currentPage);
      result.push(currentPage + 1);
      result.push('...');
      result.push(totalPages);
    }

    return result;
  };
  const visiblePages = getVisiblePages();
  const paginationItems = visiblePages.map((page, index) => ({
    key:
      page === '...'
        ? `ellipsis-${String(visiblePages[index - 1])}-${String(visiblePages[index + 1])}`
        : `page-${page}`,
    page,
  }));

  const handlePageChange = (page: number) => {
    scrollMainContentToTop();
    onPageChange(page);
  };

  const handlePerPageChange = (nextPerPage: number) => {
    scrollMainContentToTop();
    onPerPageChange(nextPerPage);
  };

  return (
    <div
      className={`pagination-container d-flex justify-content-between align-items-center ${className}`}
    >
      <div className="d-flex align-items-center gap-3">
        <CFormSelect
          value={perPage}
          onChange={(e) => handlePerPageChange(Number(e.target.value))}
          style={{ width: 'auto' }}
        >
          {perPageOptions.map((option) => (
            <option key={option} value={option}>
              {option} per page
            </option>
          ))}
        </CFormSelect>
        {total !== undefined && <span className="text-muted">{getPaginationText()}</span>}
      </div>
      {totalPages > 1 && (
        <CPagination className={paginationClassName}>
          <CPaginationItem
            disabled={!hasPrevPage}
            onClick={() => hasPrevPage && handlePageChange(currentPage - 1)}
          >
            Previous
          </CPaginationItem>
          {paginationItems.map(({ key, page }) => (
            <CPaginationItem
              key={key}
              active={typeof page === 'number' && page === currentPage}
              onClick={() => typeof page === 'number' && handlePageChange(page)}
              disabled={page === '...'}
            >
              {page}
            </CPaginationItem>
          ))}
          <CPaginationItem
            disabled={!hasNextPage}
            onClick={() => hasNextPage && handlePageChange(currentPage + 1)}
          >
            Next
          </CPaginationItem>
        </CPagination>
      )}
    </div>
  );
};
